import type { QuoteFile } from 'paintscout'
import { imageKey, type PlacedImages } from './photo-categories'

/**
 * Words too common to count as a match on their own — otherwise a slot titled "Our Services" would
 * "match" almost any photo whose DOM context happens to contain "our".
 */
const STOP_WORDS = new Set([
  'the',
  'and',
  'for',
  'with',
  'our',
  'your',
  'this',
  'that',
  'from',
  'are',
  'was',
  'were',
  'has',
  'have',
  'you',
  'all',
  'more',
  'about',
  'into',
  'over',
  'than',
  'then',
  'what',
  'when',
  'where'
])

function tokenize(text?: string): Set<string> {
  if (!text) return new Set()
  // Strip tags and HTML entities (`&amp;`, `&nbsp;`, `&#39;`, …) before extracting words — left
  // in, an entity's name (`amp`, `nbsp`) would leak into the token set as a spurious match.
  const plain = text.replaceAll(/<[^>]+>/g, ' ').replaceAll(/&[a-z#0-9]+;/gi, ' ')
  const matches: string[] = plain.toLowerCase().match(/[a-z0-9]+/g) ?? []
  const words = matches.filter((word) => word.length > 2 && !STOP_WORDS.has(word))
  return new Set(words)
}

function overlapCount(a: Set<string>, b: Set<string>): number {
  let count = 0
  for (const word of a) {
    if (b.has(word)) count++
  }
  return count
}

/**
 * Each image's own context text, preferring what the site itself associated with it (`domContext`)
 * over the vision classifier's caption — `domContext` reflects what a human already put next to
 * this specific photo, which is stronger evidence than a model's guess at the subject (#24101).
 */
function contextTokensFor(image: QuoteFile): { tokens: Set<string>; fromDomContext: boolean } {
  const domText = [image.domContext?.heading, image.domContext?.text].filter(Boolean).join(' ')
  const domTokens = tokenize(domText)
  if (domTokens.size) return { tokens: domTokens, fromDomContext: true }
  return { tokens: tokenize(image.photoCaption), fromDomContext: false }
}

/**
 * A slot with 1-2 significant words (most card titles) only needs one of them to show up in a
 * photo's context to count; a longer slot needs two, so a single generic shared word doesn't win.
 */
function matchThreshold(slotTokenCount: number): number {
  return slotTokenCount <= 2 ? 1 : 2
}

/**
 * Finds the best photo for a slot's own authored text (a card's content, a banner's heading, etc)
 * by word overlap against each candidate's scraped DOM context or classifier caption — see
 * `contextTokensFor`. This is tried ahead of the coarse `photoCategory` pool: two photos that share
 * a category (a restaurant and a church both classify as `exterior-other`) can still be told apart
 * by what the site itself said was near them, or what the photo actually depicts (#24101).
 *
 * Shares `placed` with `takeFromCategoryPool`/`createImageCursor` so a photo picked here is never
 * handed out again by either of those.
 */
export function matchImageByContext(
  images: QuoteFile[] | undefined,
  slotText?: string,
  placed?: PlacedImages
): QuoteFile | undefined {
  const slotTokens = tokenize(slotText)
  if (!images?.length || !slotTokens.size) return undefined

  const threshold = matchThreshold(slotTokens.size)
  let best: { image: QuoteFile; score: number; fromDomContext: boolean } | undefined

  for (const image of images) {
    if (placed?.has(imageKey(image))) continue

    const { tokens, fromDomContext } = contextTokensFor(image)
    if (!tokens.size) continue

    const score = overlapCount(slotTokens, tokens)
    if (score < threshold) continue

    const better = !best || score > best.score || (score === best.score && fromDomContext && !best.fromDomContext)
    if (better) {
      best = { image, score, fromDomContext }
    }
  }

  if (!best) return undefined
  placed?.add(imageKey(best.image))
  return best.image
}
